import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function Spotlight() {
  return (
    <div className="article-container">
      <div className="content-wrapper">
        <div className="article-content">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({node, ...props}) => <h1 className="title" {...props} />,
              h2: ({node, ...props}) => <h2 className="section-title" {...props} />,
              h3: ({node, ...props}) => <h3 className="subsection-title" {...props} />,
              p: ({node, ...props}) => <p className="paragraph" {...props} />,
              ul: ({node, ...props}) => <ul className="medium-list" {...props} />,
              li: ({node, ...props}) => <li className="list-item" {...props} />,
            }}
          >
            {data}
          </ReactMarkdown>
        </div>
      </div>

      <style>
        {`
          .article-container {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
            max-width: 680px;
            margin: 0 auto;
            padding: 20px;
            color: rgb(41, 41, 41);
          }

          .content-wrapper {
            line-height: 1.6;
          }

          .title {
            font-size: 48px;
            font-weight: 700;
            margin-bottom: 32px;
            line-height: 1.15;
            letter-spacing: -0.02em;
          }

          .section-title {
            font-size: 34px;
            font-weight: 700;
            margin: 48px 0 24px;
            line-height: 1.15;
            letter-spacing: -0.015em;
          }

          .subsection-title {
            font-size: 26px;
            font-weight: 700;
            margin: 36px 0 18px;
            line-height: 1.15;
            letter-spacing: -0.012em;
          }

          .paragraph {
            font-size: 20px;
            margin-bottom: 28px;
            color: rgb(41, 41, 41);
          }

          .medium-list {
            margin: 0 0 28px;
            padding-left: 40px;
            list-style-type: disc;
          }

          .list-item {
            font-size: 20px;
            margin-bottom: 12px;
            padding-left: 0;
            color: rgb(41, 41, 41);
          }

          .list-item::before {
            display: none;
          }

          .list-item a {
            color: rgb(41, 41, 41);
            text-decoration: underline;
          }

          @media (max-width: 768px) {
            .article-container {
              padding: 16px;
            }

            .title {
              font-size: 36px;
            }

            .section-title {
              font-size: 28px;
            }

            .subsection-title {
              font-size: 24px;
            }

            .paragraph,
            .list-item {
              font-size: 18px;
            }
          }
        `}
      </style>
    </div>
  );
}

export default Spotlight;

const data = `
# Introducing Autonomous AI Agents: Your Personal AI Stock Market Analysts

Ever wish you had a team of tireless stock market analysts working around the clock just for you? Today, we're thrilled to introduce Autonomous AI Agents – your personal AI research team that never sleeps, constantly monitoring the markets and delivering insights right when you need them.

## What Are Autonomous AI Agents?

Think of them as your dedicated financial research team, working 24/7 to track stocks, analyze market trends, and alert you to opportunities and risks. Unlike traditional AI chatbots that can only handle immediate questions, our Autonomous AI Agents can take on complex, long-running projects and process vast amounts of financial data.

## Why You'll Love This Feature

Our Autonomous AI Agents transform how you stay on top of market movements. You'll never miss important market moves again, as your AI team continuously monitors your chosen stocks and sends email alerts about significant events and red flags. They even keep you informed about local economic changes in regions where you're invested.

When it comes to complex research tasks, we've got you covered. Remember those times when you wanted to analyze years of earnings calls or compare multiple company filings? Our AI Agents excel at these massive research projects, breaking them down into manageable chunks and delivering comprehensive insights.

For example, want to understand how Apple's strategy has evolved over the last 20 quarters? Just ask, and our AI Agents will analyze 20 earnings calls for you, identifying key trends and shifts in company direction.

The best part? Once you set up an autonomous project, you can go about your day while our AI Agents do the heavy lifting. No more constant checking of news feeds or financial statements – we'll notify you when something needs your attention.

## Getting Started is Simple

Ready to put your AI research team to work? It couldn't be easier. Simply chat with [Lima](chat) and say something like "Monitor Apple stock and notify me of any important events and red flags." or "Monitor Apple, Google, MSFT, and META stock. Notify me if any of them start trending toward being undervalued." That's all it takes.

Your AI Agents will immediately begin planning the research strategy, breaking down the work into manageable tasks, executing the analysis, and alerting you to important findings as they happen.

## Real-World Applications

Picture having AI Agents as your personal market researchers. They can continuously track multiple stocks for potential red flags while analyzing emerging trends in sectors you're interested in. They'll process and summarize vast amounts of financial data, giving you clear, actionable insights without the information overload.

## The Future of AI-Powered Investment Research

This is just the beginning. Our Autonomous AI Agents represent a leap forward in how investors can leverage AI for deeper, more comprehensive market research. No more information overload or missed opportunities – just clear, actionable insights delivered when you need them.

## Try It Today

Ready to experience the future of AI-powered investment research? Start your first autonomous project today by chatting with [Lima](chat). Simply ask Lima to monitor a stock or group of stocks, and watch as your AI research team gets to work.

Don't let important market moves slip through the cracks. Let our Autonomous AI Agents be your eyes and ears in the market, working tirelessly to keep you informed and ahead of the curve.

*What will you have your AI research team analyze first?*
`;

